import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Close, PlayArrow, CancelOutlined, Refresh, ExpandMore } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { normalizeTask, taskStatusLabel, taskStageLabel, taskActivityLabel, canResumeTask } from './taskPresentation';

/**
 * Processing panel component to display queue status
 */
const ProcessingPanel = React.memo(({ 
  onClose, 
  currentProcessing, 
  processingQueue,
  onCancelQueueItem,
  onResumeQueueItem,
  cancellingQueueItemId,
  resumingQueueItemId,
  onRefresh,
  queueUnavailable = false,
  lastQueueRefresh = null,
}) => {
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [expandedQueueItemIds, setExpandedQueueItemIds] = React.useState({});

  const normalizeStatus = (status) => String(status || '').trim().toLowerCase();
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setIsRefreshing(false);
    }
  };
  const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
      case 'translating':
      case 'analyzing':
        return 'primary';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  // Ensure we have safe values
  const safeCurrentProcessing = currentProcessing ? {
    ...normalizeTask(currentProcessing),
    type: currentProcessing.type || 'translation',
    status: currentProcessing.status || 'pending',
    can_resume: currentProcessing.can_resume === true,
    total_requirements: currentProcessing.total_requirements ?? 0,
    translated_requirements: currentProcessing.translated_requirements ?? 0,
    analyzed_requirements: currentProcessing.analyzed_requirements ?? 0,
    processed_requirements: currentProcessing.processed_requirements ?? 0,
    created_requirements: currentProcessing.created_requirements ?? 0,
    updated_requirements: currentProcessing.updated_requirements ?? 0,
    current_step: currentProcessing.current_step || ''
  } : null;

  // Get the label and count based on process type
  const getProcessLabel = (type) => {
    switch(type) {
      case 'smart':
        return 'SMART Analysis';
      case 'import':
        return 'Import';
      case 'ttl_translation':
        return 'TTL Translation';
      default:
        return 'Translation';
    }
  };

  const getProcessedCount = (processing) => {
    switch(processing.type) {
      case 'smart':
        return processing.analyzed_requirements;
      case 'import':
        return processing.processed_requirements;
      case 'ttl_translation':
        return processing.translated_requirements ?? 0;
      default:
        return processing.translated_requirements;
    }
  };

  const getProcessedLabel = (type) => {
    switch(type) {
      case 'smart':
        return 'Analyzed requirements';
      case 'import':
        return 'Processed rows';
      case 'ttl_translation':
        return 'Literals translated';
      case 'document':
        return 'Pages extracted';
      default:
        return 'Translated requirements';
    }
  };

  const safeQueue = Array.isArray(processingQueue) ? processingQueue.map(normalizeTask) : [];

  const canCancelQueueItem = (item) => {
    const cancellableStatuses = ['pending', 'processing', 'translating', 'analyzing'];
    return cancellableStatuses.includes(normalizeStatus(item?.status));
  };

  const canResumeQueueItem = canResumeTask;

  const shouldShowStatusChip = () => true;

  const handleQueueItemToggle = (itemId) => (_, isExpanded) => {
    setExpandedQueueItemIds((previous) => ({
      ...previous,
      [itemId]: isExpanded,
    }));
  };

  const isCancellingCurrentTask =
    safeCurrentProcessing &&
    String(cancellingQueueItemId) === String(safeCurrentProcessing.id);

  const isResumingCurrentTask =
    safeCurrentProcessing &&
    String(resumingQueueItemId) === String(safeCurrentProcessing.id);
  const currentTaskIsCancelling = Boolean(isCancellingCurrentTask);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Processing</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onRefresh && (
            <Tooltip title="Refresh progress">
              <span>
                <IconButton 
                  aria-label="Refresh progress"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  size="small"
                >
                  {isRefreshing ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Refresh fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton onClick={onClose} aria-label="Close processing panel">
            <Close />
          </IconButton>
        </Box>
      </Box>

      {(queueUnavailable || lastQueueRefresh) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {queueUnavailable ? 'Updates temporarily unavailable. Showing saved progress.' : taskActivityLabel({ last_activity_at: lastQueueRefresh })}
        </Typography>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Current Processing */}
      {safeCurrentProcessing && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Latest activity
          </Typography>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    flexBasis: '100%',
                    pr: 1,
                    minWidth: 0,
                    display: 'block',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                  }}
                >
                  {safeCurrentProcessing.filename}
                </Typography>
                {onResumeQueueItem && canResumeQueueItem(safeCurrentProcessing) && (
                  <Tooltip title="Resume from saved progress">
                    <span>
                      <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        disabled={isResumingCurrentTask}
                        onClick={() => onResumeQueueItem(safeCurrentProcessing)}
                        sx={{ mr: 0.5, minWidth: 0 }}
                        startIcon={isResumingCurrentTask ? null : <PlayArrow fontSize="small" />}
                      >
                        {isResumingCurrentTask ? <CircularProgress size={16} color="inherit" /> : 'Resume'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {onCancelQueueItem && (
                  <Tooltip title="Cancel current task">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={!canCancelQueueItem(safeCurrentProcessing) || isCancellingCurrentTask}
                        onClick={() => onCancelQueueItem(safeCurrentProcessing)}
                        sx={{ mr: 0.5 }}
                      >
                        {isCancellingCurrentTask ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CancelOutlined fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {currentTaskIsCancelling && (
                    <Chip
                      label="Cancelling safely..."
                      size="small"
                      color="warning"
                    />
                  )}
                  {getProcessLabel(safeCurrentProcessing.type) && (
                    <Chip 
                      label={getProcessLabel(safeCurrentProcessing.type)}
                      size="small" 
                      variant="outlined"
                      color="info"
                    />
                  )}
                  {shouldShowStatusChip(safeCurrentProcessing.type) && (
                    <Chip 
                      label={taskStatusLabel(safeCurrentProcessing)}
                      size="small"
                      color={safeCurrentProcessing.recovery_state === 'publication_unknown' ? 'default' : getStatusColor(safeCurrentProcessing.status)}
                    />
                  )}
                </Box>
              </Box>
              
              {/* Progress Details */}
              {safeCurrentProcessing.total_requirements > 0 && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {getProcessedLabel(/^doc_(upload|reload)_/.test(safeCurrentProcessing.id) ? 'document' : safeCurrentProcessing.type)}
                    </Typography>
                    <Typography variant="caption" fontWeight="bold" color="primary.main">
                      {getProcessedCount(safeCurrentProcessing)} / {safeCurrentProcessing.total_requirements}
                    </Typography>
                  </Box>
                  
                  {/* Import-specific details */}
                  {safeCurrentProcessing.type === 'import' && !/^doc_(upload|reload)_/.test(safeCurrentProcessing.id) && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="caption" fontWeight="bold" color="success.main">
                          {safeCurrentProcessing.created_requirements}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Updated
                        </Typography>
                        <Typography variant="caption" fontWeight="bold" color="info.main">
                          {safeCurrentProcessing.updated_requirements}
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Remaining
                    </Typography>
                    <Typography variant="caption" fontWeight="bold" color="warning.main">
                      {Math.max(0, safeCurrentProcessing.total_requirements - getProcessedCount(safeCurrentProcessing))}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {/* Progress Bar */}
              <Box sx={{ mt: 1 }}>
                {/* Cambio a estado indeterminado si la tarea se reporta al 0% pero ya está activa. 
                  Esto evita la mala experiencia de usuario de ver una barra vacía durante minutos 
                  mientras Gemini procesa el primer lote masivo.
                */}
                {safeCurrentProcessing.progress !== null && safeCurrentProcessing.status !== 'pending' && (
                  <LinearProgress
                    variant="determinate"
                    value={safeCurrentProcessing.progress}
                    aria-label="Saved progress"
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                )}
                {safeCurrentProcessing.progress !== null && safeCurrentProcessing.status !== 'pending' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {safeCurrentProcessing.progress.toFixed(1)}%
                  </Typography>
                )}
                {taskActivityLabel(safeCurrentProcessing) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {taskActivityLabel(safeCurrentProcessing)}
                  </Typography>
                )}
                {safeCurrentProcessing.recovery_state === 'active' && Number(safeCurrentProcessing.recovery_attempts) > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Continuing saved progress
                  </Typography>
                )}
              </Box>
              
              {/* Current Step */}
              {currentTaskIsCancelling ? (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="warning.dark" sx={{ wordBreak: 'break-word' }}>
                    Cancellation requested
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    {taskStageLabel(safeCurrentProcessing)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Queue */}
      {safeQueue.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Other tasks ({safeQueue.length})
          </Typography>
          {safeQueue.map((item, index) => (
            (() => {
              const itemIsCancelling = String(cancellingQueueItemId) === String(item.id);

              return (
            <Accordion
              key={item.id || `queue-${index}`}
              elevation={1}
              expanded={Boolean(expandedQueueItemIds[item.id])}
              onChange={handleQueueItemToggle(item.id)}
              sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`${item.id || index}-content`}
                id={`${item.id || index}-header`}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0.5 } }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', width: '100%', minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      flexBasis: '100%',
                      pr: 1,
                      minWidth: 0,
                      display: 'block',
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                      lineHeight: 1.4,
                    }}
                  >
                    {item.filename || item.name || 'Unknown file'}
                  </Typography>
                  {itemIsCancelling && (
                    <Chip label="Cancelling safely..." size="small" color="warning" sx={{ mr: 0.5, flexShrink: 0 }} />
                  )}
                  {onResumeQueueItem && canResumeQueueItem(item) && (
                    <Tooltip title="Resume from saved progress">
                      <span>
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          disabled={resumingQueueItemId === item.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            onResumeQueueItem(item);
                          }}
                          sx={{ mr: 0.5, minWidth: 0 }}
                          startIcon={resumingQueueItemId === item.id ? null : <PlayArrow fontSize="small" />}
                        >
                          {resumingQueueItemId === item.id ? <CircularProgress size={16} color="inherit" /> : 'Resume'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {onCancelQueueItem && (
                    <Tooltip title={itemIsCancelling ? "Waiting for a safe cancellation point" : "Cancel task"}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!canCancelQueueItem(item) || itemIsCancelling}
                          onClick={(event) => {
                            event.stopPropagation();
                            onCancelQueueItem(item);
                          }}
                          sx={{ mr: 0.5, flexShrink: 0 }}
                        >
                          {itemIsCancelling ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <CancelOutlined fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {item.type && getProcessLabel(item.type) && (
                      <Chip label={getProcessLabel(item.type)} size="small" variant="outlined" color="info" />
                    )}
                    <Chip label={taskStatusLabel(item)} size="small" color={item.recovery_state === 'publication_unknown' ? 'default' : getStatusColor(item.status)} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="caption" fontWeight="bold">{getProcessLabel(item.type) || 'Queued task'}</Typography>
                </Box>

                {typeof item.progress_percentage === 'number' && (
                  <Box sx={{ mt: 1, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Progress</Typography>
                      <Typography variant="caption" fontWeight="bold" color="primary.main">
                        {Number(item.progress_percentage).toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(0, Number(item.progress_percentage) || 0))}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                )}

                {item.current_step && (
                  <Box sx={{ mt: 1, mb: 1.5, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {taskStageLabel(item)}
                    </Typography>
                  </Box>
                )}

                {taskActivityLabel(item) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {taskActivityLabel(item)}
                  </Typography>
                )}

                {item.created_at && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Queued</Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {new Date(item.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                )}

                {item.size && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Size</Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {item.size >= 1024 * 1024
                        ? `${(item.size / (1024 * 1024)).toFixed(1)} MB`
                        : `${Math.max(1, Math.round(item.size / 1024))} KB`}
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
              );
            })()
          ))}
        </Box>
      )}

      {/* Empty state */}
      {!safeCurrentProcessing && safeQueue.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {queueUnavailable ? 'Task status unavailable.' : 'No pending tasks.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

ProcessingPanel.displayName = 'ProcessingPanel';

ProcessingPanel.propTypes = {
  onClose: PropTypes.func,
  currentProcessing: PropTypes.object,
  processingQueue: PropTypes.array,
  onCancelQueueItem: PropTypes.func,
  onResumeQueueItem: PropTypes.func,
  cancellingQueueItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resumingQueueItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRefresh: PropTypes.func,
  queueUnavailable: PropTypes.bool,
  lastQueueRefresh: PropTypes.string,
};

export default ProcessingPanel;